import React, { useState, useEffect } from 'react';
import { Fragment } from 'react';

import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import { PlanRepository } from '../../MedicareProAPI/Repositories/PlanRepository';
import { Utilities } from '../../MedicareProAPI/Utilities/Utilities';
import CommissionSingle from '../Components/CommissionSingle';
import LineChart from '../Components/Charts/LineChart';
import PieChart from '../Components/Charts/PieChart';
import BarChart from '../Components/Charts/BarChart';
import Loader from '../Components/Loader';

export default function AgentAnalyticsRenewalsPanel(props) {
  const [commissionScheduleMonths, setCommissionScheduleMonths] = useState(null);
  const [commissionScheduleIssuedTotals, setCommissionScheduleIssuedTotals] = useState(null);
  const [commissionScheduleWithPendingTotals, setCommissionScheduleWithPendingTotals] =
    useState(null);
  const [commissionScheduleCompanies, setCommissionScheduleCompanies] = useState(null);
  const [commissionScheduleCompanyTotals, setCommissionScheduleCompanyTotals] = useState(null);

  const [commissionScheduleTypes, setCommissionScheduleTypes] = useState(null);
  const [commissionScheduleTypeTotals, setCommissionScheduleTypeTotals] = useState(null);

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.userAccessToken);

  // Get User Plan types on first load
  useEffect(() => {
    //getUserCommissionsScheduleTotals
    webAPI.getUserCommissionsScheduleTotals(props.userId).then((commissionSchedule) => {
      setCommissionScheduleMonths(commissionSchedule.commissionScheduleMonths);
      setCommissionScheduleIssuedTotals(commissionSchedule.commissionScheduleIssuedTotals);
      setCommissionScheduleWithPendingTotals(
        commissionSchedule.commissionScheduleWithPendingTotals
      );

      setCommissionScheduleCompanies(commissionSchedule.commissionScheduleCompanies);
      setCommissionScheduleCompanyTotals(commissionSchedule.commissionScheduleCompanyTotals);

      setCommissionScheduleTypes(commissionSchedule.commissionScheduleTypes);
      setCommissionScheduleTypeTotals(commissionSchedule.commissionScheduleTypeTotals);

      // Method returns all of these values in object
    });
  }, []);

  return (
    <>
      <div className="row">
        {/* Renewal Chain */}
        <div className="col-sm-12 col-md-8 px-2 sm-resp">
          <div className="card card-tall">
            <div className="card-header font-weight-bold py-2">
              Renewal Chain
              <strong className="float-right text-primary mr-2 small">With Pending</strong>
              <strong className="float-right text-success mr-2 small">Issued</strong>
            </div>
            <div className="card-body pt-1">
              {commissionScheduleMonths &&
              commissionScheduleIssuedTotals &&
              commissionScheduleWithPendingTotals ? (
                commissionScheduleIssuedTotals.length > 0 &&
                commissionScheduleWithPendingTotals.length > 0 ? (
                  <LineChart
                    className="chart-labels-x-rotated chart-labels-bold chart-renewals"
                    data={{
                      labels: commissionScheduleMonths,
                      series: [commissionScheduleIssuedTotals, commissionScheduleWithPendingTotals],
                    }}
                    options={{
                      height: '400px',
                      axisY: {
                        low: Math.min(...commissionScheduleIssuedTotals) * 0.95,
                        high: Math.max(...commissionScheduleIssuedTotals) * 1.05,
                        labelInterpolationFnc: (value) => formatter.format(value),
                      },
                    }}
                  />
                ) : (
                  <div data-alert className="alert alert-info mx-3 mb-0">
                    Not enough data to show this chart.
                  </div>
                )
              ) : (
                <Loader size="large" />
              )}
            </div>
          </div>
        </div>

        {/* Current Renewals */}
        <div className="col-sm-12 col-md-4 px-0 py-0">
          <div sx={{ cursor: 'pointer' }} className=" col-sm-12 col-md-12 px-2 sm-resp ">
            <div className="card card-tall text-blacktext mb-0" sx={{ height: '140px' }}>
              <div className="card-header font-weight-bold py-2 ">
                Current Renewals <i className="font-weight-light">(pm)</i>
              </div>
              <div className="card-body pt-4" sx={{ overflowY: 'hidden !important' }}>
                <div className="row" sx={{ marginTop: '-10px' }}>
                  <div className="col-3">
                    <i className="fal fa-badge-dollar fa-3x text-success align-self-top"></i>
                  </div>
                  <div className="col-9 money">
                    {commissionScheduleIssuedTotals ? (
                      <h2
                        className="font-weight-bold  text-right align-middle mt-1"
                        id="successCount"
                      >
                        {commissionScheduleIssuedTotals.length
                          ? formatter.format(commissionScheduleIssuedTotals[0])
                          : formatter.format(0)}
                      </h2>
                    ) : (
                      <Loader size="large" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Type Breakdown */}
        <div className="col-sm-12 col-md-4 px-2 sm-resp">
          <div className="card card-tall">
            <div className="card-header font-weight-bold py-2">Renewals By Type</div>
            <div className="card-body pt-1 pb-0 pl-0 pr-0">
              {commissionScheduleTypes && commissionScheduleTypeTotals ? (
                commissionScheduleTypeTotals.length > 0 ? (
                  <BarChart
                    className="chart-labels-bold"
                    currency={true}
                    data={{
                      labels: commissionScheduleTypes,
                      series: commissionScheduleTypeTotals,
                    }}
                    options={{
                      height: '400px',
                      distributeSeries: true,
                      axisY: {
                        showLabel: false,
                      },
                    }}
                  />
                ) : (
                  <div data-alert className="alert alert-info mx-3 mb-0">
                    Not enough data to show this chart.
                  </div>
                )
              ) : (
                <Loader size="large" />
              )}
            </div>
          </div>
        </div>

        {/* Company Breakdown */}
        <div className="col-sm-12 col-md-8 px-2 sm-resp">
          <div className="card card-tall">
            <div className="card-header font-weight-bold py-2">Renewals By Company</div>
            <div className="card-body pt-1 pb-0 pl-0 pr-0">
              {commissionScheduleCompanies && commissionScheduleCompanyTotals ? (
                commissionScheduleCompanyTotals.length > 0 ? (
                  <BarChart
                    className="chart-labels-bold"
                    currency={true}
                    data={{
                      labels: commissionScheduleCompanies,
                      series: commissionScheduleCompanyTotals,
                    }}
                    options={{
                      height: '400px',
                      distributeSeries: true,
                      axisY: {
                        showLabel: false,
                      },
                    }}
                  />
                ) : (
                  <div data-alert className="alert alert-info mx-3 mb-0">
                    Not enough data to show this chart.
                  </div>
                )
              ) : (
                <Loader size="large" />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
